import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { NavigationService } from "src/services/navigation/navigation.service";
import { Item, ItemCategory, SelectableItem } from "src/services/item/item.interface";
import { itemCategoryToString, itemToString } from "src/services/item/item-display";
import { User } from "src/services/user/user.interface";
import { camelCaseToDisplayString } from "src/services/utils/string.utils";

@Component({
  selector: "rc-item-grid",
  template: `
    @if (loading) {
      <ion-skeleton-text [animated]="true"></ion-skeleton-text>
    }
    @if (!loading) {
      @for (category of categoryGroups; track category) {
        <ion-item-divider class="container-outline" [sticky]="true">
          <ion-label>{{ itemCategoryToString(category) }}</ion-label>
        </ion-item-divider>
        <ion-grid>
          <ion-row>
            @for (item of itemsByCategory.get(category); track item) {
              <ion-col size-xs="6" size-md="3" class="ion-text-center">
                <ion-card class="container-shadow">
                  <ion-card-content>
                    <div class="card-container" (click)="navigationService.goToItemDetailsPage(item.id)">
                      @if (!item.isFav) {
                        <ion-icon
                          name="heart-outline"
                          class="item-icon clickable"
                          (click)="toggleFavItem(item); $event.stopPropagation()"></ion-icon>
                      }
                      @if (item.isFav) {
                        <ion-icon
                          name="heart-sharp"
                          class="item-icon clickable"
                          (click)="toggleFavItem(item); $event.stopPropagation()"></ion-icon>
                      }
                      @if (item.image) {
                        <cdn-img size="sm" class="item-picture" [src]="item.image" alt="item picture"></cdn-img>
                      }
                      @if (!item.image) {
                        <ion-icon class="item-picture-placeholder" name="image-outline" color="medium"></ion-icon>
                      }
                    </div>
                    @if (showAddRemoveButtons) {
                      @if (!item.selected && item.status !== "reclaimed" && item.status !== "inTrip") {
                        <ion-button
                          class="add-remove-button"
                          size="small"
                          color="secondary"
                          (click)="onClick(item, true); $event.stopPropagation()">
                          <ion-icon name="add-circle-outline"></ion-icon>
                        </ion-button>
                      }
                      @if (item.selected && item.status !== "reclaimed" && item.status !== "inTrip") {
                        <ion-button
                          class="add-remove-button"
                          size="small"
                          color="success"
                          (click)="onClick(item, false); $event.stopPropagation()">
                          <ion-icon name="checkmark-outline"></ion-icon>
                        </ion-button>
                      }
                      @if (item.status === "reclaimed" || item.status === "inTrip") {
                        <ion-button class="add-remove-button" size="small" color="secondary" [disabled]="true">
                          {{ camelCaseToDisplayString(item.status) }}
                        </ion-button>
                      }
                    }
                  </ion-card-content>
                </ion-card>
                <ion-label>{{ itemToString(item) }}</ion-label>
              </ion-col>
            }
          </ion-row>
        </ion-grid>
      }
    }
  `,
  styleUrls: ["./item-grid.component.scss"],
  standalone: false,
})
export class ItemGridComponent implements OnChanges {
  @Input() public items: SelectableItem[];
  public itemsByCategory = new Map<ItemCategory | null, SelectableItem[]>();
  public categoryGroups: (ItemCategory | null)[] = [];
  public loading: boolean = true;
  @Output() onClickAddRemove = new EventEmitter<SelectableItem>();
  @Input() public showAddRemoveButtons: boolean = false;
  @Input() public user: User;
  protected readonly itemCategoryToString = itemCategoryToString;
  protected readonly itemToString = itemToString;
  protected readonly camelCaseToDisplayString = camelCaseToDisplayString;

  constructor(public readonly navigationService: NavigationService) {}

  public ngOnChanges(): void {
    this.sortItems();
    this.loading = false;
  }

  public toggleFavItem(item: Item): void {
    item.isFav = !item.isFav;
  }

  public onClick(item: SelectableItem, selected: boolean): void {
    item.selected = selected;

    this.onClickAddRemove.emit(item);
  }

  private sortItems(): void {
    this.itemsByCategory = new Map<ItemCategory | null, SelectableItem[]>();
    this.categoryGroups = [];

    this.items.forEach((item: SelectableItem) => {
      const itemCategory = item.category;
      if (item.photo !== undefined) {
        item.image = item.photo;
      }

      let categoryItems = this.itemsByCategory.get(itemCategory);
      if (!categoryItems) {
        categoryItems = [];
        this.itemsByCategory.set(itemCategory, categoryItems);
        this.categoryGroups.push(itemCategory);
      }

      categoryItems.push(item);
    });
  }
}
